import { useEffect, useMemo, useState } from 'react';
import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { api } from '../lib/api.js';
import {
  currentNativeNetworkProvider,
  subscribeNativeNetworkProvider,
} from '../mobile/platform.js';

export type NetworkTransport =
  | 'satellite' | 'cellular' | 'wifi' | 'ethernet' | 'vpn' | 'offline' | 'unknown';

interface NativeNetworkStatus {
  connected: boolean;
  validated: boolean;
  transport: NetworkTransport;
  carrier: string | null;
  metered: boolean;
  roaming: boolean;
  downstreamKbps: number;
  upstreamKbps: number;
}

interface NetworkPathPlugin {
  getStatus(): Promise<NativeNetworkStatus>;
  addListener(
    eventName: 'networkStatusChange',
    listener: (status: NativeNetworkStatus) => void,
  ): Promise<PluginListenerHandle>;
}

type NavigatorConnection = {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
  downlink?: number;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
};

export interface NetworkPathState extends NativeNetworkStatus {
  label: string;
  detail: string;
  verified: boolean;
  native: boolean;
  profile: 'full' | 'balanced' | 'constrained' | 'offline';
  refreshMs: number;
}

const NetworkPath = registerPlugin<NetworkPathPlugin>('NetworkPath');

function browserStatus(): NativeNetworkStatus {
  const connected = typeof navigator === 'undefined' || navigator.onLine !== false;
  const connection = typeof navigator === 'undefined'
    ? undefined
    : (navigator as Navigator & { connection?: NavigatorConnection }).connection;
  const type = connection?.type;
  const transport: NetworkTransport = !connected ? 'offline'
    : type === 'wifi' ? 'wifi'
      : type === 'cellular' ? 'cellular'
        : type === 'ethernet' ? 'ethernet' : 'unknown';
  return {
    connected,
    validated: connected,
    transport,
    carrier: null,
    metered: Boolean(connection?.saveData || type === 'cellular'),
    roaming: false,
    downstreamKbps: Math.max(0, Math.round((connection?.downlink ?? 0) * 1_000)),
    upstreamKbps: 0,
  };
}

function normalizeNative(value: NativeNetworkStatus): NativeNetworkStatus {
  const validTransport: NetworkTransport[] = [
    'satellite', 'cellular', 'wifi', 'ethernet', 'vpn', 'offline', 'unknown',
  ];
  const connected = Boolean(value.connected);
  return {
    connected,
    validated: Boolean(value.validated),
    transport: connected && validTransport.includes(value.transport) ? value.transport : 'offline',
    carrier: typeof value.carrier === 'string' ? value.carrier.slice(0, 80) : null,
    metered: Boolean(value.metered),
    roaming: Boolean(value.roaming),
    downstreamKbps: Number.isFinite(value.downstreamKbps)
      ? Math.max(0, Math.round(value.downstreamKbps)) : 0,
    upstreamKbps: Number.isFinite(value.upstreamKbps)
      ? Math.max(0, Math.round(value.upstreamKbps)) : 0,
  };
}

function describe(
  status: NativeNetworkStatus,
  serverProvider: 'starlink' | 'other' | 'unknown',
  native: boolean,
): NetworkPathState {
  if (!status.connected) return {
    ...status, label: 'Offline', detail: 'Calls are unavailable', verified: true,
    native, profile: 'offline', refreshMs: 60_000,
  };

  const tMobile = /(^|\W)(t[ -]?mobile|tmobile)(\W|$)/iu.test(status.carrier ?? '');
  if (status.transport === 'satellite') return {
    ...status,
    label: tMobile ? 'T-Satellite via Starlink' : 'Satellite',
    detail: 'Verified by device', verified: true, native,
    profile: 'constrained', refreshMs: 60_000,
  };
  if (serverProvider === 'starlink' && ['wifi', 'ethernet', 'unknown'].includes(status.transport)) {
    return {
      ...status, label: 'Starlink', detail: 'Verified network', verified: true, native,
      profile: 'balanced', refreshMs: 30_000,
    };
  }
  if (status.transport === 'cellular') return {
    ...status, label: status.carrier || 'Mobile data',
    detail: status.roaming ? 'Cellular, roaming' : 'Cellular',
    verified: native, native, profile: status.metered ? 'constrained' : 'balanced',
    refreshMs: status.metered ? 45_000 : 30_000,
  };
  if (status.transport === 'wifi') return {
    ...status, label: 'Wi-Fi', detail: 'Internet connected', verified: native,
    native, profile: status.metered ? 'balanced' : 'full', refreshMs: status.metered ? 30_000 : 15_000,
  };
  if (status.transport === 'vpn') return {
    ...status, label: 'VPN', detail: 'Protected network path', verified: native,
    native, profile: 'balanced', refreshMs: 30_000,
  };
  if (status.transport === 'ethernet') return {
    ...status, label: 'Ethernet', detail: 'Wired connection', verified: native,
    native, profile: 'full', refreshMs: 15_000,
  };
  return {
    ...status, label: 'Internet connected', detail: native ? 'Transport not reported' : 'Browser estimate',
    verified: false, native, profile: status.metered ? 'constrained' : 'balanced',
    refreshMs: status.metered ? 45_000 : 30_000,
  };
}

export function useNetworkPath(): NetworkPathState {
  const native = Capacitor.isNativePlatform();
  const [status, setStatus] = useState<NativeNetworkStatus>(browserStatus);
  const [serverProvider, setServerProvider] = useState<'starlink' | 'other' | 'unknown'>(
    currentNativeNetworkProvider,
  );

  useEffect(() => {
    let stopped = false;
    let nativeHandle: PluginListenerHandle | undefined;
    const updateBrowser = () => { if (!stopped) setStatus(browserStatus()); };
    const connection = (navigator as Navigator & { connection?: NavigatorConnection }).connection;
    window.addEventListener('online', updateBrowser);
    window.addEventListener('offline', updateBrowser);
    connection?.addEventListener?.('change', updateBrowser);

    if (native) {
      void NetworkPath.getStatus().then((value) => {
        if (!stopped) setStatus(normalizeNative(value));
      }).catch(updateBrowser);
      void NetworkPath.addListener('networkStatusChange', (value) => {
        if (!stopped) setStatus(normalizeNative(value));
      }).then((handle) => {
        if (stopped) void handle.remove();
        else nativeHandle = handle;
      }).catch(() => undefined);
    }
    return () => {
      stopped = true;
      window.removeEventListener('online', updateBrowser);
      window.removeEventListener('offline', updateBrowser);
      connection?.removeEventListener?.('change', updateBrowser);
      void nativeHandle?.remove();
    };
  }, [native]);

  useEffect(() => {
    if (!native) return undefined;
    setServerProvider(currentNativeNetworkProvider());
    return subscribeNativeNetworkProvider(setServerProvider);
  }, [native]);

  useEffect(() => {
    const canVerifyWithServer = typeof window !== 'undefined'
      && window.location.protocol === 'https:'
      && window.location.hostname === 'x2f-planning.com';
    if (native || !status.connected || !canVerifyWithServer) {
      setServerProvider('unknown');
      return;
    }
    let stopped = false;
    const verify = () => {
      void api.networkPath().then((result) => {
        if (!stopped) setServerProvider(result.provider);
      }).catch(() => {
        if (!stopped) setServerProvider('unknown');
      });
    };
    verify();
    const timer = window.setInterval(verify, 5 * 60_000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [status.connected, status.transport]);

  return useMemo(
    () => describe(status, serverProvider, native),
    [status, serverProvider, native],
  );
}
